// Generic counter details modal — shows transactions of a given type + total.
// Special types (savings, recurring) are delegated to sub-components.
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { CounterDetailsSavings } from "@/components/dashboard/CounterDetailsSavings";
import { CounterDetailsRecurring } from "@/components/dashboard/CounterDetailsRecurring";

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString();
const MONTH_TYPES = new Set(["expense"]);

function monthStart() {
  const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0);
  return d.toLocaleDateString("en-CA");
}

export function CounterDetails({ open, type, title, onClose }) {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const ccy = user?.currency || "AED";
  const AR = lang === "ar";
  useEffect(() => {
    if (!open || !type || type === "savings" || type === "recurring") return;
    setRows(null);
    const p = new URLSearchParams({ type, limit: "200" });
    if (MONTH_TYPES.has(type)) p.set("start", monthStart());
    api.get(`/transactions?${p}`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [open, type]);

  if (!open) return null;
  if (type === "savings") return <CounterDetailsSavings onClose={onClose} />;
  if (type === "recurring") return <CounterDetailsRecurring onClose={onClose} />;

  const total = (rows || []).reduce((s, r) => s + Number(r.final_amount || 0), 0);
  const period = MONTH_TYPES.has(type) ? (AR ? "هذا الشهر" : "This month") : (AR ? "الإجمالي التراكمي" : "Cumulative total");

  return (
    <div data-testid="counter-details-modal" dir={AR ? "rtl" : "ltr"} className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm grid place-items-end sm:place-items-center p-3" onClick={onClose}>
      <div className="w-full sm:max-w-md neu p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display text-lg font-extrabold neu-accent-blue">{title}</h3>
            <p className="text-[11px] text-slate-500">{period}</p>
          </div>
          <button data-testid="counter-details-close" onClick={onClose} className="w-9 h-9 rounded-full neu-sm grid place-items-center active:scale-95"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 rounded-2xl neu-inset mb-3 flex items-center justify-between">
          <span className="text-sm font-bold">{AR ? "المجموع" : "Total"}</span>
          <span className="font-display font-extrabold text-lg tabular-nums neu-accent-blue">{fmt(total)} {ccy}</span>
        </div>
        {rows === null && <p className="text-sm text-slate-500 py-6 text-center">…</p>}
        {rows?.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">{AR ? "لا معاملات" : "No transactions"}</p>}
        <div className="space-y-1.5">
          {rows?.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 p-2.5 rounded-xl neu-sm text-sm">
              <div className="min-w-0 flex-1">
                <p className="truncate">{r.description || r.category}</p>
                <p className="text-[10px] text-slate-400 tabular-nums">{r.date}</p>
              </div>
              <span className="tabular-nums font-bold shrink-0">{fmt(r.final_amount)} {r.final_currency}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
